import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  PenLine, History as HistoryIcon, FileText, Layers, LogOut,
  Users, Building2, CreditCard, LayoutDashboard, Flame, Snowflake, Settings as SettingsIcon,
  LifeBuoy,
} from "lucide-react";

const ROLE_ORDER = ["employee", "supervisor", "org_admin", "system_admin"];
const atLeast = (role, min) => ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(min);
const localToday = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user, logout, trialDaysLeft } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const days = trialDaysLeft();
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/logs/streak", { params: { local_date: localToday() } })
      .then((res) => { if (active) setStreak(res.data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const nav = [
    { to: "/app/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard", show: true },
    { to: "/app/log", label: t("nav.dailyLog"), icon: PenLine, testid: "nav-daily-log", show: true },
    { to: "/app/history", label: t("nav.history"), icon: HistoryIcon, testid: "nav-history", show: true },
    { to: "/app/reports", label: t("nav.reports"), icon: FileText, testid: "nav-reports", show: true },
    { to: "/app/team", label: t("nav.teamReports"), icon: Users, testid: "nav-team", show: user?.account_type === "organization" && user?.can_view_team === true },
    { to: "/app/context", label: t("nav.context"), icon: Layers, testid: "nav-context", show: true },
    { to: "/app/organization", label: t("nav.organization"), icon: Building2, testid: "nav-org", show: user?.account_type === "organization" && (user?.can_manage_org === true || user?.is_primary_admin === true || user?.is_org_admin === true) },
    { to: "/app/billing", label: t("nav.billing"), icon: CreditCard, testid: "nav-billing", show: user?.account_type !== "organization" || user?.is_primary_admin === true || user?.is_org_admin === true },
    { to: "/app/help", label: t("nav.help"), icon: LifeBuoy, testid: "nav-help", show: true },
    { to: "/app/settings", label: t("nav.settings"), icon: SettingsIcon, testid: "nav-settings", show: true },
  ].filter((n) => n.show);

  return (
    <aside
      className={`w-64 shrink-0 bg-[#F7F7F8] text-[#202123] border-r border-[#E5E5E5] flex flex-col z-50 fixed inset-y-0 left-0 h-screen overflow-y-auto md:sticky md:top-0 md:h-auto md:min-h-screen md:overflow-visible transition-transform duration-300 ease-in-out md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      data-testid="sidebar"
    >
      <div className="px-6 py-6 border-b border-[#E5E5E5]">
        <div className="flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/app/log")}>
            <img src="/jotbay-logo.png" alt="JotBay" className="h-9 w-auto" data-testid="sidebar-logo" />
          </div>
          <NotificationBell />
        </div>
        <p className="text-[11px] text-[#6E6E80] mt-2 leading-snug">{t("nav.tagline")}</p>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                isActive ? "bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" : "text-[#6E6E80] hover:bg-[#ECECEC] hover:text-[#202123]"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-[#E5E5E5] space-y-3">
        {streak && streak.current > 0 && (
          <button onClick={() => navigate("/app/log")} data-testid="sidebar-streak"
            className="w-full flex items-center gap-2.5 rounded-md bg-gradient-to-r from-amber-500/20 to-orange-500/10 px-3 py-2.5 hover:from-amber-500/30 transition-colors text-left">
            <Flame className="h-5 w-5 text-orange-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#202123] leading-none flex items-center gap-1.5" data-testid="sidebar-streak-count">
                {t("nav.streak.count", { count: streak.current })}
                {streak.used_freeze && <Snowflake className="h-3.5 w-3.5 text-sky-300" data-testid="sidebar-freeze-icon" />}
              </p>
              <p className="text-[11px] text-[#6E6E80] mt-0.5">
                {streak.logged_today ? t("nav.streak.loggedToday") : t("nav.streak.keepIt")}
              </p>
            </div>
          </button>
        )}
        {user?.subscription_status === "trialing" && days !== null && (
          <div className="rounded-md bg-white border border-[#E5E5E5] px-3 py-2.5" data-testid="trial-badge">
            <p className="text-[11px] uppercase tracking-wide text-[#202123] font-mono">{t("nav.trial.label")}</p>
            <p className="text-sm text-[#202123]">{t("nav.trial.daysLeft", { count: days })}</p>
          </div>
        )}
        {user?.subscription_status === "read_only" && (
          <div className="rounded-md bg-amber-500/20 px-3 py-2.5" data-testid="readonly-badge">
            <p className="text-[11px] uppercase tracking-wide text-amber-300 font-mono">{t("nav.readonly.label")}</p>
            <p className="text-xs text-[#202123]">{t("nav.readonly.subscribe")}</p>
          </div>
        )}
        {user?.payment_past_due && !user?.payment_suspended && user?.payment_retry_at && (user?.account_type !== "organization" || user?.is_primary_admin === true || user?.is_org_admin === true) && (() => {
          const d = new Date(user.payment_retry_at);
          if (isNaN(d.getTime())) return null;
          const days = Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86400000));
          return (
            <button onClick={() => navigate("/app/billing")} data-testid="retry-chip"
              className="w-full text-left rounded-md bg-red-100 border border-red-200 px-3 py-2.5 hover:bg-red-200/70 transition-colors">
              <p className="text-[11px] uppercase tracking-wide text-red-700 font-mono">{t("nav.retry.label")}</p>
              <p className="text-xs text-red-800">{t("nav.retry.days", { count: days })}</p>
            </button>
          );
        })()}
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[#ECECEC] flex items-center justify-center text-sm font-semibold">
            {(user?.full_name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user?.full_name}</p>
            <p className="text-[11px] text-[#6E6E80] truncate">{user?.email}</p>
            <span
              data-testid="account-type-badge"
              className={`mt-1 inline-block text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded ${
                user?.account_type === "organization" ? "bg-[#ECECEC] text-[#202123]" : "bg-[#ECECEC] text-[#6E6E80]"
              }`}
            >
              {user?.account_type === "organization" ? t("nav.account.organization") : t("nav.account.individual")}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <LanguageSwitcher variant="compact" />
        </div>
        <button onClick={logout} data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 rounded-md border border-[#C5C5D0] px-3 py-2 text-sm font-medium text-[#6E6E80] hover:bg-[#ECECEC] hover:text-[#202123] transition-colors">
          <LogOut className="h-[18px] w-[18px]" />
          {t("nav.signOut")}
        </button>
      </div>
    </aside>
  );
}
